/**
 * Boon & Boon — manual AUD price override
 *
 * BUCKS Currency Converter auto-converts INR prices for every currency by
 * live exchange rate. For AUD specifically, we want to show a manually set
 * price instead (set per-product via the manual_aud_price /
 * manual_aud_compare_price metafields). This script does nothing for any
 * other currency — BUCKS's own conversion is left untouched.
 *
 * How BUCKS reports the active currency (found via localStorage inspection):
 *   - localStorage.buckscc_last_manual   → set when the customer manually
 *     picks a currency from the switcher. Manual switches trigger a full
 *     page reload, so a single check on load covers that case.
 *   - localStorage.buckscc_auto_currency → set by geolocation detection.
 *     On a first-time visit this can resolve a moment after page load.
 *   Priority: an explicit manual pick wins over auto-detection.
 *
 * Two element patterns are supported:
 *   [data-aud-price] / [data-aud-compare]
 *     Simple 1:1 swap — used on the homepage cards, collection grid,
 *     product page, and cart line item unit price. The manual price is
 *     shown exactly as typed.
 *   [data-aud-unit-price] / [data-aud-unit-compare]
 *     Used on cart LINE TOTALS, where the manual per-unit price needs
 *     multiplying by that row's quantity. Quantity is read from the
 *     nearest .quantity__input inside the same .bnb-cart__row.
 *
 * After per-row values are settled, the cart footer subtotal is
 * recomputed by summing every row's currently-displayed line total —
 * this works regardless of whether each row is manually overridden or
 * left to BUCKS's own conversion, since it just reads what's already on
 * screen. It only touches the footer at all if at least one row on the
 * page has a manual override; otherwise BUCKS's own total is left alone.
 *
 * Why a MutationObserver: BUCKS appears to keep adjusting price-related
 * elements after its own initial pass (likely to avoid a flash of
 * unconverted price on slow connections), which can silently revert our
 * override. Rather than guess at timing, we watch for further changes
 * and re-apply whenever something goes out of sync. Every apply function
 * only writes when the DOM doesn't already match the desired value, so
 * this settles immediately and does not loop — including when our own
 * writes trigger the observer again.
 */
(function () {
  function getActiveCurrency() {
    try {
      var manualRaw = window.localStorage.getItem('buckscc_last_manual');
      if (manualRaw) {
        var manual = JSON.parse(manualRaw);
        if (manual && manual.currency) return manual.currency;
      }
    } catch (e) {}

    try {
      var autoRaw = window.localStorage.getItem('buckscc_auto_currency');
      if (autoRaw) {
        var auto = JSON.parse(autoRaw);
        if (auto && auto.currency) return auto.currency;
      }
    } catch (e) {}

    return null;
  }

  function resolvePriceAndCompareEls(row) {
    // Prefer our own price/compare classes.
    var priceEl = row.querySelector('.bb-sku__price');
    var compareEl = row.querySelector('.bb-sku__compare');
    if (priceEl) return { priceEl: priceEl, compareEl: compareEl };

    // Fall back to Dawn's standard price snippet classes (card-product.liquid).
    // ".price-item--regular" is the CURRENT price when there's no sale, but
    // becomes the crossed-out ORIGINAL price once ".price-item--sale" also
    // exists — so sale presence decides which element is which.
    var saleEl = row.querySelector('.price-item--sale');
    var regularEl = row.querySelector('.price-item--regular');
    if (saleEl) return { priceEl: saleEl, compareEl: regularEl };
    return { priceEl: regularEl, compareEl: null };
  }

  function setText(el, text) {
    if (el && el.textContent !== text) {
      el.textContent = text;
      return true;
    }
    return false;
  }

  function setVisible(el, visible) {
    var desired = visible ? '' : 'none';
    if (el && el.style.display !== desired) {
      el.style.display = desired;
      return true;
    }
    return false;
  }

  // Simple 1:1 swap for [data-aud-price] rows.
  function applySimpleRows() {
    var rows = document.querySelectorAll('[data-aud-price]');
    if (!rows.length) return false;

    var changedAny = false;
    rows.forEach(function (row) {
      var audPrice = row.getAttribute('data-aud-price');
      var audCompare = row.getAttribute('data-aud-compare');
      if (!audPrice) return;

      var els = resolvePriceAndCompareEls(row);
      if (setText(els.priceEl, audPrice)) changedAny = true;
      if (els.compareEl) {
        if (audCompare) {
          if (setText(els.compareEl, audCompare)) changedAny = true;
          if (setVisible(els.compareEl, true)) changedAny = true;
        } else if (setVisible(els.compareEl, false)) {
          changedAny = true;
        }
      }
    });
    return changedAny;
  }

  // Extract the first numeric amount from a formatted string, e.g.
  // "$59.99 AUD" → 59.99. Tolerant of thousands separators.
  function parseAmount(str) {
    if (!str) return null;
    var m = String(str).replace(/,/g, '').match(/(\d+(\.\d+)?)/);
    return m ? parseFloat(m[1]) : null;
  }

  // Rebuild a formatted string with a new amount, keeping the merchant's
  // own prefix/suffix (currency symbol, " AUD" label, etc.) intact.
  function formatWithTemplate(template, amount) {
    var formatted = amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return template.replace(/[\d,]+\.?\d*/, formatted);
  }

  // Quantity-aware multiplication for [data-aud-unit-price] cart line totals.
  function applyLineTotals() {
    var rows = document.querySelectorAll('[data-aud-unit-price]');
    if (!rows.length) return false;

    var changedAny = false;
    rows.forEach(function (row) {
      var unitPriceTpl = row.getAttribute('data-aud-unit-price');
      var unitCompareTpl = row.getAttribute('data-aud-unit-compare');
      var unitAmount = parseAmount(unitPriceTpl);
      if (unitAmount === null) return;

      var cartRow = row.closest('.bnb-cart__row') || row.parentElement;
      var qtyInput = cartRow ? cartRow.querySelector('.quantity__input') : null;
      var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
      if (!qty || qty < 1) qty = 1;

      var els = resolvePriceAndCompareEls(row);
      var lineTotalText = formatWithTemplate(unitPriceTpl, unitAmount * qty);
      if (setText(els.priceEl, lineTotalText)) changedAny = true;

      if (els.compareEl) {
        var compareAmount = parseAmount(unitCompareTpl);
        if (compareAmount !== null) {
          var lineCompareText = formatWithTemplate(unitCompareTpl, compareAmount * qty);
          if (setText(els.compareEl, lineCompareText)) changedAny = true;
          if (setVisible(els.compareEl, true)) changedAny = true;
        } else if (setVisible(els.compareEl, false)) {
          changedAny = true;
        }
      }
    });
    return changedAny;
  }

  // Recompute the cart footer subtotal by summing every row's currently
  // displayed line total — only when at least one row has a manual
  // override, so BUCKS's own total is left alone otherwise.
  function applyCartFooterTotal() {
    var footerEl = document.querySelector('.totals__total-value');
    if (!footerEl) return false;

    var hasAnyOverride = document.querySelector('[data-aud-price], [data-aud-unit-price]');
    if (!hasAnyOverride) return false;

    var rowTotalEls = document.querySelectorAll('.bnb-cart__row-total .bb-sku__price');
    if (!rowTotalEls.length) return false;

    var sum = 0;
    var sampleTemplate = null;
    rowTotalEls.forEach(function (el) {
      var amount = parseAmount(el.textContent);
      if (amount !== null) {
        sum += amount;
        if (!sampleTemplate) sampleTemplate = el.textContent;
      }
    });
    if (!sampleTemplate) return false;

    var newTotalText = formatWithTemplate(sampleTemplate, sum);
    return setText(footerEl, newTotalText);
  }

  function applyManualAUD() {
    if (getActiveCurrency() !== 'AUD') return false;
    var changed = false;
    if (applySimpleRows()) changed = true;
    if (applyLineTotals()) changed = true;
    if (applyCartFooterTotal()) changed = true;
    return changed;
  }

  function init() {
    applyManualAUD();

    // Auto (geolocation) detection can resolve slightly after load on a
    // first-time visit, so poll briefly in addition to the observer below.
    var attempts = 0;
    var maxAttempts = 20; // ~6 seconds at 300ms
    var interval = setInterval(function () {
      attempts++;
      applyManualAUD();
      if (attempts >= maxAttempts) clearInterval(interval);
    }, 300);

    // Re-assert values if anything else (BUCKS's own script, quantity
    // changes re-rendering the cart, variant switches, etc.) changes these
    // elements after our initial pass. Every apply function above is
    // idempotent — only writing when something is actually out of sync —
    // so this settles immediately and never loops.
    var observer = new MutationObserver(function () {
      applyManualAUD();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'value'],
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();